import { flare } from "@flareapp/js";
import { flareVue } from "@flareapp/vue";

export default defineNuxtPlugin((nuxtApp) => {
  if (process.env.NODE_ENV === "production" && !isDomainDev()) {
    flare.light(nuxtApp.$config.public.flareKey);

    flare.beforeEvaluate = (error: Error) => {
      if (isUnAuthorizedError(error)) return false;

      if (isFetchError(error)) {
        if (isServerError(error)) return false;
        else if (isGatewayTimeoutError(error)) return false;
        else if (isNoProductsFoundError(error)) return false;
        else if (isCantDeleteShipmentError(error)) return false;
        else if (isCantDeletePrinterError(error)) return false;
        else if (isCheckoutTestFailedError(error)) return false;
        else if (isSearchValidationError(error)) return false;
        else if (isPalletScanBusinessError(error)) return false;
      } else if (isModuleFetchError(error)) return false;
      else if (isSyntaxError8(error)) return false;

      return error;
    };

    flare.beforeSubmit = (report) => {
      return {
        ...report,
        application_path: report.context.request?.url,
      };
    };

    nuxtApp.vueApp.use(flareVue);
  }
});

const getHttpStatusCode = (error: any) => {
  return error.statusCode ?? error.status ?? error.data?.statusCode;
};

const isModuleFetchError = (error: Error) => {
  return (
    error.message?.includes("dynamically imported module") ||
    error.message?.includes("Importing a module script failed") ||
    error.message?.includes("error loading dynamically imported module") ||
    error.message?.includes("Unable to preload CSS for")
  );
};

const isFetchError = (error: any) => {
  // FetchError always has statusCode and data.url properties
  return typeof error.statusCode === "number" && typeof error.data?.url === "string";
};

const isNoProductsFoundError = (error: any) => {
  return [400, 404].includes(getHttpStatusCode(error)) && error.data?.url?.startsWith("/api/products");
};

const isCantDeleteShipmentError = (error: any) => {
  const url = error.data?.url ?? error.response?.url ?? "";
  return getHttpStatusCode(error) == 400 && url.startsWith("/api/shipments");
};

const isCantDeletePrinterError = (error: any) => {
  const url = error.data?.url ?? error.response?.url ?? "";
  return getHttpStatusCode(error) == 400 && /^\/api\/workspace\/printers\/[^/]+$/.test(url);
};

const isCheckoutTestFailedError = (error: any) => {
  return getHttpStatusCode(error) == 400 && error.data?.url?.startsWith("/api/workspace/checkouts/test");
};

const isUnAuthorizedError = (error: any) => {
  return getHttpStatusCode(error) == 401;
};

const isServerError = (error: any) => {
  return getHttpStatusCode(error) == 500;
};

const isGatewayTimeoutError = (error: any) => {
  return getHttpStatusCode(error) == 504;
};

const isSyntaxError8 = (error: Error) => {
  return error.name == "SyntaxError" && error.message == "8";
};

const isSearchValidationError = (error: any) => {
  return getHttpStatusCode(error) == 422 && error.data?.url?.startsWith("/api/search");
};

const isPalletScanBusinessError = (error: any) => {
  return (
    [490, 491, 492, 493, 494, 495, 500].includes(getHttpStatusCode(error)) &&
    error.data?.url?.includes("/pallets/scan/add")
  );
};
